import { Component, Input, OnInit } from "@angular/core";
import { ModalController } from "@ionic/angular";
import DOMPurify from "dompurify";
import { readEml } from "eml-parse-js";
import { ReadedEmlJson } from "eml-parse-js/dist/interface";
import { NgxExtendedPdfViewerService, PDFNotificationService } from "ngx-extended-pdf-viewer";
import { MailmanagerEmailResponse } from "../../../api/models/mailmanager-email-response";
import { MailmanagerMailboxEmailContentAttachmentResponse } from "../../../api/models/mailmanager-mailbox-email-content-attachment-response";
import { MailmanagerMailboxEmailContentResponse } from "../../../api/models/mailmanager-mailbox-email-content-response";
import { updateTitleByOrginalTitle } from "../../global-functions/global-functions";
import { DownloadService } from "../../services/download.service";
import { LoveItAlertService } from "../../services/love-it-alert.service";

enum FileType {
  Pdf = "Pdf",
  Image = "Image",
  Email = "Email",
  Unsupported = "Unsupported",
}

@Component({
  selector: "file-viewer-bearbeiten",
  templateUrl: "./file-viewer.component.html",
  styleUrls: ["./file-viewer.component.scss"],
  standalone: false,
})
export class FileViewerComponent implements OnInit {
  @Input() fileSource: string;
  @Input() fileName: string;
  @Input() edit: boolean;

  faircheckSecondaryColor: string;
  currentFileType: FileType;
  currentFileExtension: string;
  parsedEml: ReadedEmlJson | null = null;
  emailHeader: MailmanagerEmailResponse | null = null;
  emailContent: MailmanagerMailboxEmailContentResponse | null = null;
  emlFileBlob: Blob | null = null;

  private stampRotationState: {
    originalBitmapUrl: string;
    originalRectWidth: number;
    originalRectHeight: number;
    currentAngleDeg: number;
    lastCenterX: number;
    lastCenterY: number;
  } | null = null;

  constructor(
    private modalController: ModalController,
    private pdfViewerService: NgxExtendedPdfViewerService,
    private pdfNotificationService: PDFNotificationService,
    private loveItAlertService: LoveItAlertService,
    private downloadService: DownloadService,
  ) {}

  ngOnInit() {
    this.setupFileByType();
  }

  setupFileByType() {
    this.currentFileExtension = this.fileName.split(".").pop().toLowerCase();

    if (this.currentFileExtension === "pdf") {
      this.currentFileType = FileType.Pdf;
      this.faircheckSecondaryColor = getComputedStyle(document.documentElement).getPropertyValue(
        "--ion-color-secondary",
      );
      return;
    }

    const imageExtensions = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]);
    if (!!this.currentFileExtension && imageExtensions.has(this.currentFileExtension)) {
      this.currentFileType = FileType.Image;
      return;
    }

    if (this.downloadService.isEmlFile(this.fileName)) {
      this.currentFileType = FileType.Email;
      this.parseEmail();
      return;
    }

    this.currentFileType = FileType.Unsupported;
  }

  setInputColorById(id: string) {
    const editorInkColorElement = document.querySelector<HTMLInputElement>(id);
    editorInkColorElement.value = this.faircheckSecondaryColor;

    const inputEvent = new Event("input", {
      bubbles: true,
      cancelable: true,
    });
    editorInkColorElement.dispatchEvent(inputEvent);
  }

  updatePlaceholderById(id: string, newPlaceholder: string) {
    const element = document.querySelector(`[id="${id}"]:not(pdf-shy-button)`);
    if (element) {
      element.setAttribute("placeholder", newPlaceholder);
    }
  }

  updateInnerHtmlByDatal10nid(id: string, newInnerHtml: string) {
    const element = document.querySelector(`[data-l10n-id="${id}"]:not(pdf-shy-button)`);
    if (element) {
      element.innerHTML = newInnerHtml;
    }
  }

  updateInnerHtmlById(id: string, newInnerHtml: string, selectedPartToReplace: string = "") {
    const element = document.querySelector(`[id="${id}"]:not(pdf-shy-button)`);
    if (element) {
      if (selectedPartToReplace == "") {
        element.innerHTML = newInnerHtml;
      } else {
        const currentHtml = element.innerHTML;
        const index = currentHtml.indexOf(selectedPartToReplace);
        if (index !== -1) {
          element.innerHTML =
            currentHtml.substring(0, index) +
            newInnerHtml +
            currentHtml.substring(index + selectedPartToReplace.length);
        }
      }
    }
  }

  translatePdfEditorTooltips() {
    updateTitleByOrginalTitle("Toggle Sidebar", "Seitenmenü umschalten");
    updateTitleByOrginalTitle("Show Thumbnails", "Vorschaubilder anzeigen");
    updateTitleByOrginalTitle("Find", "Suche");
    updateTitleByOrginalTitle("Find in Document", "Im Dokument suchen");
    this.updatePlaceholderById("findInput", "Im Dokument suchen...");
    updateTitleByOrginalTitle(
      "Find the previous occurrence of the phrase",
      "Finde das vorherigen Vorkommen der Phrase",
    );
    updateTitleByOrginalTitle("Find the next occurrence of the phrase", "Finde das nächste Vorkommen der Phrase");
    this.updateInnerHtmlByDatal10nid("pdfjs-find-highlight-checkbox", "Alle hervorheben");
    this.updateInnerHtmlByDatal10nid("pdfjs-find-match-case-checkbox-label", "Groß- und Kleinschreibung beachten");
    this.updateInnerHtmlByDatal10nid("pdfjs-find-match-diacritics-checkbox-label", "Umlaute beachten");
    this.updateInnerHtmlByDatal10nid("pdfjs-find-entire-word-checkbox-label", "Suche als ganze Wörter");
    updateTitleByOrginalTitle("Go to First Page", "Springe zur ersten Seite");
    updateTitleByOrginalTitle("Previous Page", "Vorherige Seite");
    updateTitleByOrginalTitle("Page", "Seite");
    this.updateInnerHtmlById("numPages", "von", "of");
    updateTitleByOrginalTitle("Next Page", "Nächste Seite");
    updateTitleByOrginalTitle("Go to Last Page", "Springe zur letzten Seite");
    updateTitleByOrginalTitle("Zoom Out", "Ansicht verkleinern");
    updateTitleByOrginalTitle("Zoom In", "Ansicht vergrößern");
    this.updateInnerHtmlById("autoOption", "Automatische Größe");
    this.updateInnerHtmlById("pageActualOption", "Tatsächliche Größe");
    this.updateInnerHtmlById("pageFitOption", "Passende Größe");
    this.updateInnerHtmlById("pageWidthOption", "Passende Breite");
    updateTitleByOrginalTitle("Switch to Presentation Mode", "In den Präsentationsmodus wechseln");
    updateTitleByOrginalTitle("Switch to Presentation Mode", "In den Präsentationsmodus wechseln");
    updateTitleByOrginalTitle("Rotate Clockwise", "Drehen im Uhrzeigersinn");
    updateTitleByOrginalTitle("Rotate Anti-Clockwise", "Drehen gegen den Uhrzeigersinn");
    updateTitleByOrginalTitle("Draw", "Zeichnen");

    const thumbPageTitles = document.querySelectorAll('[title*="Page"]');
    thumbPageTitles.forEach((elem) => {
      const currentTitle = elem.getAttribute("title");
      const index = currentTitle.indexOf("Page");
      const replaceString = "Seite ";
      if (index !== -1) {
        elem.setAttribute(
          "title",
          currentTitle.substring(0, index) + replaceString + currentTitle.substring(index + replaceString.length),
        );
      }
    });
  }

  async addImage(urlOrDataUrl, left, bottom, right, top, rotation): Promise<void> {
    const stampAnnotation: any = {
      annotationType: 13,
      bitmapUrl: urlOrDataUrl,
      rect: [200, 450, 200 + right, 450 + top],
      rotation: rotation,
    };

    await this.pdfViewerService.addEditorAnnotation(stampAnnotation);
    this.pdfViewerService.switchAnnotationEdtorMode(13);
  }

  undo() {
    const pdfViewerApplication = this.pdfNotificationService.onPDFJSInitSignal();
    if (pdfViewerApplication) {
      pdfViewerApplication.eventBus.dispatch("editingaction", { source: this, name: "undo" });
    }
  }

  redo() {
    const pdfViewerApplication = this.pdfNotificationService.onPDFJSInitSignal();
    if (pdfViewerApplication) {
      pdfViewerApplication.eventBus.dispatch("editingaction", { source: this, name: "redo" });
    }
  }

  async rotateSelectedStamp(): Promise<void> {
    const selectedElement = document.querySelector(".stampEditor.selectedEditor") as HTMLElement;
    if (!selectedElement) {
      return;
    }

    const pdfViewerApplication = this.pdfNotificationService.onPDFJSInitSignal() as any;
    if (!pdfViewerApplication) {
      return;
    }

    const uiManager = pdfViewerApplication.pdfViewer?._layerProperties?.annotationEditorUIManager;
    if (!uiManager) {
      return;
    }

    const editor = uiManager.getEditor(selectedElement.id);
    if (!editor) {
      return;
    }

    const serialized = editor.serialize(true);
    if (!serialized || serialized.annotationType !== 13 || !serialized.bitmapUrl) {
      return;
    }

    const [left, bottom, right, top] = serialized.rect;
    const centerX = (left + right) / 2;
    const centerY = (bottom + top) / 2;

    const isSameStamp =
      this.stampRotationState &&
      Math.abs(this.stampRotationState.lastCenterX - centerX) < 1 &&
      Math.abs(this.stampRotationState.lastCenterY - centerY) < 1;

    let originalBitmapUrl: string;
    let originalRectWidth: number;
    let originalRectHeight: number;
    let newAngleDeg: number;

    if (isSameStamp) {
      originalBitmapUrl = this.stampRotationState.originalBitmapUrl;
      originalRectWidth = this.stampRotationState.originalRectWidth;
      originalRectHeight = this.stampRotationState.originalRectHeight;
      newAngleDeg = (this.stampRotationState.currentAngleDeg + 45) % 360;
    } else {
      originalBitmapUrl = serialized.bitmapUrl;
      originalRectWidth = right - left;
      originalRectHeight = top - bottom;
      newAngleDeg = 45;
    }

    editor.remove();

    if (newAngleDeg === 0) {
      await this.pdfViewerService.addEditorAnnotation({
        annotationType: 13,
        bitmapUrl: originalBitmapUrl,
        rect: [
          centerX - originalRectWidth / 2,
          centerY - originalRectHeight / 2,
          centerX + originalRectWidth / 2,
          centerY + originalRectHeight / 2,
        ],
        rotation: serialized.rotation,
        pageIndex: serialized.pageIndex,
      } as any);
    } else {
      const angleRad = (newAngleDeg * Math.PI) / 180;
      const result = await this.rotateStampBitmap(originalBitmapUrl, angleRad, originalRectWidth, originalRectHeight);
      await this.pdfViewerService.addEditorAnnotation({
        annotationType: 13,
        bitmapUrl: result.bitmapUrl,
        rect: [
          centerX - result.newWidth / 2,
          centerY - result.newHeight / 2,
          centerX + result.newWidth / 2,
          centerY + result.newHeight / 2,
        ],
        rotation: serialized.rotation,
        pageIndex: serialized.pageIndex,
      } as any);
    }

    this.stampRotationState = {
      originalBitmapUrl,
      originalRectWidth,
      originalRectHeight,
      currentAngleDeg: newAngleDeg,
      lastCenterX: centerX,
      lastCenterY: centerY,
    };

    this.pdfViewerService.switchAnnotationEdtorMode(13);
  }

  private rotateStampBitmap(
    dataUrl: string,
    angleRad: number,
    rectWidth: number,
    rectHeight: number,
  ): Promise<{ bitmapUrl: string; newWidth: number; newHeight: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Normalize bitmap to match PDF rect aspect ratio so rotation geometry is correct
        const pdfAspect = rectWidth / rectHeight;
        let normW: number;
        let normH: number;
        if (pdfAspect >= 1) {
          normW = Math.max(img.width, img.height);
          normH = Math.round(normW / pdfAspect);
        } else {
          normH = Math.max(img.width, img.height);
          normW = Math.round(normH * pdfAspect);
        }
        const minSize = 50;
        if (Math.min(normW, normH) < minSize) {
          const upscale = minSize / Math.min(normW, normH);
          normW = Math.round(normW * upscale);
          normH = Math.round(normH * upscale);
        }

        const normCanvas = document.createElement("canvas");
        normCanvas.width = normW;
        normCanvas.height = normH;
        normCanvas.getContext("2d").drawImage(img, 0, 0, normW, normH);

        // Rotate
        const cosA = Math.abs(Math.cos(angleRad));
        const sinA = Math.abs(Math.sin(angleRad));
        const rotW = Math.ceil(normW * cosA + normH * sinA);
        const rotH = Math.ceil(normW * sinA + normH * cosA);
        const rotCanvas = document.createElement("canvas");
        rotCanvas.width = rotW;
        rotCanvas.height = rotH;
        const rotCtx = rotCanvas.getContext("2d");
        rotCtx.translate(rotW / 2, rotH / 2);
        rotCtx.rotate(angleRad);
        rotCtx.drawImage(normCanvas, -normW / 2, -normH / 2);

        // Trim transparent pixels
        const trimmed = this.trimTransparentPixels(rotCanvas);

        // Convert trimmed pixel dimensions back to PDF units
        const pdfPerPixel = rectWidth / normW;
        resolve({
          bitmapUrl: trimmed.toDataURL("image/png"),
          newWidth: trimmed.width * pdfPerPixel,
          newHeight: trimmed.height * pdfPerPixel,
        });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  private trimTransparentPixels(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 0) {
          if (x < minX) {
            minX = x;
          }
          if (y < minY) {
            minY = y;
          }
          if (x > maxX) {
            maxX = x;
          }
          if (y > maxY) {
            maxY = y;
          }
        }
      }
    }

    if (maxX < minX) {
      return canvas;
    }

    const tw = maxX - minX + 1;
    const th = maxY - minY + 1;
    const trimmed = document.createElement("canvas");
    trimmed.width = tw;
    trimmed.height = th;
    trimmed.getContext("2d").drawImage(canvas, minX, minY, tw, th, 0, 0, tw, th);
    return trimmed;
  }

  cancelFileViewerModal() {
    this.modalController.dismiss(null, "cancel");
  }

  confirmEditModal = async () => {
    const alert = await this.loveItAlertService.create({
      header: "Änderungen speichern?",
      message: "Gespeicherte Änderungen können nicht mehr rückgängig gemacht werden.",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-secondary",
          text: "Speichern",
          role: "confirm",
          handler: async () => {
            await this.modalController.dismiss(await this.pdfViewerService.getCurrentDocumentAsBlob(), "confirm");
          },
        },
      ],
    });

    alert.present();
  };

  downloadFileBlob() {
    this.downloadService.handleDownloadedFile(this.fileSource, this.fileName);
  }

  parseEmail(): void {
    this.emlFileBlob = this.downloadService.getDownloadedFileBlobByString(this.fileSource, this.fileName);

    this.emlFileBlob
      .text()
      .then((emlText) => {
        this.parsedEml = readEml(emlText) as ReadedEmlJson;
        this.emailHeader = this.getEmailHeaderObject();
        this.emailContent = this.getEmailContentObject();
      })
      .catch(() => {
        this.emailHeader = null;
        this.emailContent = null;
        this.emlFileBlob = null;
      });
  }

  getEmailHeaderObject(): MailmanagerEmailResponse {
    if (!this.parsedEml) {
      return null;
    }

    return {
      from: this.parsedEml.headers?.From,
      to: this.parsedEml.headers?.To,
      cc: this.parsedEml.headers?.Cc,
      subject: this.parsedEml?.subject,
      date: this.parsedEml?.date as string,
    };
  }

  getEmailContentObject(): MailmanagerMailboxEmailContentResponse {
    if (!this.parsedEml) {
      return null;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.parsedEml.html, "text/html");
    const bodyContent = DOMPurify.sanitize(doc.body.innerHTML);
    const mailText = this.parsedEml.text ?? bodyContent ?? "";
    const formattedMailText = mailText.replace(/\r/g, "").replace(/\n/g, "<br>");

    return {
      attachments: this.parsedEml.attachments?.map(
        (entry) =>
          ({
            content: entry.data64,
            name: entry.name,
          }) as MailmanagerMailboxEmailContentAttachmentResponse,
      ),
      message: formattedMailText,
    } as MailmanagerMailboxEmailContentResponse;
  }

  protected readonly FileType = FileType;
}
